/**
 * intros_instances.ts — BIBLIOTHEQUE.V4.K (K1, DEC-STYLE-BOITES-V5-1) : une intro par INSTANCE d'une vue d'étape, écrite à partir de la rubrique,
 * de l'étape et du profil — ce que la boîte montre ici (l'objet de la rubrique, un chiffre clé quand la rubrique en donne un) et pourquoi ça compte
 * maintenant (le moment de l'étape) ou ce qu'on attend du lecteur (selon le profil). Français simple, registre des bulles de Carlos, ≤ 30 mots, aucun
 * code interne. Les intros de la bibliothèque (intros.ts) restent celles des exemples ; en vue d'étape, Boite (riche.tsx) appelle introInstance().
 * Marquée « exemple » comme avant (INTRO_SRC) : le vrai texte sera produit par le bot en charge (BOITES.INTRO.1).
 */
import { INTROS } from "./intros";
import production from "../../../../../scripts/bibliotheque_v4/production.json";  // lot A : production.json reste au prototype

const PROD: any = production;
/** nom court de l'étape (production.json), sans le préfixe de phase (« Cahier : », « Soumission : »…) */
const NOMS_ETAPES: Record<string, string> = Object.fromEntries((PROD.etapes || []).map((e: any) => [e.code, String(e.nom || e.code).replace(/^(Cahier|Soumission|Comparaison|Livrable)\s*:\s*/i, "").replace(/\s*\([^)]*\)\s*$/, "")]));
/** le moment de l'étape : pourquoi ça compte maintenant */
const QUAND: Record<string, string> = {
  A0: "à l'ouverture du dossier",
  A1: "dès la fiche de départ",
  A2: "avant la rencontre 1",
  A3: "pour préparer la rencontre 1",
  A4: "pendant la rencontre 1",
  A5: "à la fin de la rencontre 1",
  A6: "entre les deux rencontres",
  A7: "pendant la rencontre 2",
  A8: "à la fin de la rencontre 2",
  BL: "dans le diagnostic light",
  BC: "dans le diagnostic complet",
  C1: "pour cerner le membre recherché",
  C2: "avant de diffuser le dossier anonyme",
  C3: "avant de fixer les critères",
  C4: "au moment d'envoyer l'appel",
  C5: "pendant les mains levées",
  C6: "au classement des candidats",
  C7: "au choix du top 3",
  C8: "pour la session de jumelage",
  C9: "pour les visites",
  D1: "au départ de la soumission",
  D2: "après la visite",
  D3: "au concept proposé",
  D4: "aux exigences et à la conformité",
  D5: "au budget de la soumission",
  D6: "à la propriété intellectuelle",
  D7: "aux tolérances des intrants",
  D8: "aux limites de fourniture",
  D9: "au protocole d'essais",
  D10: "à la formation",
  D11: "au dépôt de la soumission",
  D12: "dans la comparaison ligne par ligne",
  D13: "dans la comparaison des budgets",
  D14: "pour les questions aux fournisseurs",
  D15: "au choix du fournisseur",
  E1: "dans la vue d'ensemble du cahier",
  E2: "dans l'introduction du cahier",
  E3: "dans l'analyse du client",
  E4: "dans le diagnostic du cahier",
  E5: "dans l'analyse du projet",
  E6: "dans l'ingénierie et le concept",
  E7: "dans le budget et le financement",
  E8: "pour les demandes de subvention",
  E9: "dans les conclusions d'Usine Bleue",
  E10: "avant l'approbation et la signature",
  V1: "pour le document livré",
  V2: "pour le tableur de Frank",
  V3: "pour la présentation",
  V4: "pour le code de Tim",
};
/** ce qu'on attend du lecteur, selon le profil */
const ATTENTE: Record<string, string> = { expert: "vérifiez avant de partager", client: "corrigez ce qui ne va pas", fournisseur: "complétez votre partie", document: "lecture seule", meme: "même vue pour tous" };

type Ctx = { etape: string; quand: string; nomEtape: string; code: string; lib: string; libCourt: string; profil: string; attente: string; n: number; recus: number; etats: number; montant?: string; pct?: string; premiere?: string };
const nombre = (n: number, un: string, des: string) => `${n} ${n > 1 ? des : un}`;
/** l'objet de la rubrique en minuscule, coupé à la première ponctuation (jamais au milieu d'un mot) */
const court = (lib: string) => { let s = lib.replace(/S\.M\.A\.R\.T\./g, "SMART").replace(/^AJOUT\s*[—-]\s*/i, "").replace(/^\d+\.\s*/, "").split(/\s[;:(—]\s|\s\(|\s;|\s:/)[0].trim(); const mots = s.split(/\s+/); if (mots.length > 7) s = mots.slice(0, 7).join(" ") + "…"; return s && /[a-zà-ÿ]/.test(s[1] || "") ? s[0].toLowerCase() + s.slice(1) : s || lib; };  // ≤ 30 mots au total (DEC-STYLE-BOITES-V5-1) : l'objet de la rubrique tient en 7 mots, coupé entre deux mots
function contexte(boite: string, rub: any, profil: string | null): Ctx {
  const d = rub.donnees || {}; const lignes: string[] = (d.ub_bruts && d.ub_bruts.length ? d.ub_bruts.flatMap((u: any) => (u.contenu || []).slice(1)) : d.lignes || []).map((x: string) => (x || "").trim()).filter(Boolean);
  const texte = lignes.join(" \n ");
  const m = texte.match(/\d[\d\s ]*(?:[.,]\d+)?\s*(?:k\s*)?\$/); const p = texte.match(/\d{1,3}\s*%/);
  const recus = lignes.filter((l) => /\s(Reçue?|Confirmée?|Acceptée?|Validée?)$/i.test(l)).length; const etats = lignes.filter((l) => /\s(Reçue?|Pas reçue?|Confirmée?|Acceptée?|Validée?|Proposée?|Déclarée?)$/i.test(l)).length;
  const prof = profil || "expert";
  return { etape: rub.etape, quand: QUAND[rub.etape] || `à l'étape ${NOMS_ETAPES[rub.etape] || rub.etape}`, nomEtape: NOMS_ETAPES[rub.etape] || rub.etape, code: rub.code, lib: rub.lib, libCourt: court(rub.lib), profil: prof, attente: ATTENTE[prof] || ATTENTE.expert, n: lignes.length, recus, etats, montant: m ? m[0].replace(/\s+/g, " ").trim() : undefined, pct: p ? p[0] : undefined, premiere: lignes[0] };
}
/** l'objet montré par chaque boîte, avec un chiffre clé quand la rubrique en donne un */
const OBJET: Record<string, string> = {
  F01: "le sujet traité et le résultat visé",
  F02: "les chiffres connus, avec leur source et leur état",
  F03: "l'écart entre la situation et la cible, indicateur par indicateur",
  F06: "où vous vous situez face aux repères du secteur",
  F07: "le portrait de l'entreprise, tel que nous le connaissons",
  F13: "les questions encore ouvertes",
  F14: "ce qui manque encore pour terminer l'étape",
  F17: "les documents attendus ou livrés",
  F18: "où en est chaque section et ce qui reste à faire",
  F20: "ce que la recherche a trouvé et ce qui reste à recouper",
  F25: "qui signe quoi, et quand",
  F32: "les options comparées, avec leurs forces",
  F33: "les recommandations retenues",
  F34: "les pistes retenues et leur niveau",
  F39: "la décision à prendre et ses options",
  F40: "la grille de pointage des candidats",
  F42: "les risques repérés, avec leur gravité",
  F44: "la fiche du candidat",
  F45: "l'appel lancé au réseau et où il en est",
  F46: "ce qu'on vous demande de préparer",
  F49: "qui fait quoi",
  F50: "le tableau de bord et ses indicateurs",
  F51: "les essais prévus et leurs critères",
  F52: "ce qu'on sait, ce qu'on propose et ce qu'on décide",
  F55: "les coûts, les gains et le retour",
  F56: "le calendrier, phase par phase",
  M1: "ce que coûte l'inaction",
  M2: "les trois axes de turbulence et un geste pour chacun",
  M3: "la conformité des réponses, exigence par exigence",
  M4: "le calcul des gains à aller chercher et ses hypothèses",
  M5: "le financement suivi, programme par programme",
  M7: "les questions posées aux fournisseurs",
  M9: "les deux versions comparées",
  M11: "ce que le souffleur note, pour vous seul",
  M12: "les observations de la visite",
  M13: "le rendez-vous et ce qu'il faut préparer",
  M15: "les dossiers à ouvrir",
  N01: "les défis repérés et la question que chacun pose",
  N02: "les enjeux et les objectifs, en vis-à-vis",
  N03: "les projets candidats et leurs notes",
  N04: "la fiche de l'usine : description, flux et chiffres",
  N05: "les postes de gains à chiffrer",
  N06: "la ligne du temps du projet",
  N07: "l'indice SMART et sa lecture",
  N08: "le plan annoté",
  N09: "les besoins et les enjeux du projet",
  N10: "les spécifications des intrants et des produits",
  N11: "les volumes et les scénarios de capacité",
  N12: "l'étude des temps, étape par étape",
  N13: "la simulation de productivité",
  N16: "ce qu'il faut préparer pour la session",
  N17: "les étapes actuelles de fabrication",
  N21: "la mission, la vision et les facteurs de succès",
  N22: "les gains, les pertes et le retour",
  W1: "les périphériques, un bloc par équipement",
  W2: "la séquence de fonctionnement",
  W3: "l'analyse d'Usine Bleue, sous-système par sous-système",
  W4: "les contraintes du projet",
  O1: "l'appel de jumelage reçu",
  O2: "votre main levée",
  O3: "le top 3 et les NDA",
  O4: "la session de jumelage planifiée",
  O5: "vos jumelages en cours",
  O6: "les réglages de votre Scout",
  G02: "le formulaire à remplir",
  G03: "la maturité technologique et le palier",
  G04: "le projet à trois niveaux",
  G05: "ce qui part vers les membres",
  G06: "le profil du membre recherché",
  G07: "les sections anti-litige",
  G08: "l'estimation, poste par poste",
  G09: "ce qui est noté pendant la rencontre",
  G11: "les jauges du projet",
  F05: "ce que chaque problème coûte",
};
/** l'intro d'une instance ; undefined hors d'une vue d'étape (la bibliothèque garde intros.ts) */
export function introInstance(boite: string, rub: any, profil: string | null): string | undefined {
  if (!rub) return undefined;
  // C3 b : sans étape (rubrique du greffier ou ancien bloc de la discussion), la phrase serait trouée (« … ; à l'étape , corrigez ce qui ne
  // va pas. ») — aucune intro plutôt qu'une phrase cassée
  if (!String(rub.etape || "").trim()) return undefined;
  const c = contexte(boite, rub, profil);
  const objet = OBJET[boite] || (INTROS[boite] ? INTROS[boite].c.replace(/^Voici\s+/i, "").replace(/\.\s*$/, "") : "ce que montre cette boîte");
  // un chiffre clé quand la rubrique en donne un et que la boîte le montre tel quel (tâches faites, montant, nombre de sections)
  const chiffre = boite === "F46" && c.etats ? ` ; ${c.recus} ${c.recus > 1 ? "tâches sont faites" : "tâche est faite"} sur ${c.etats}` : (boite === "M4" || boite === "M1" || boite === "G08" || boite === "F55") && c.montant ? `, soit ${c.montant}` : boite === "F18" && c.n ? ` (${nombre(c.n, "section", "sections")})` : "";
  // ≤ 30 mots (DEC-STYLE-BOITES-V5-1) : on raccourcit d'abord l'objet à sa première proposition, puis on retire le chiffre, jamais au milieu d'un mot
  const mots = (s: string) => s.trim().split(/\s+/).length;
  const variantes = [objet, objet.split(/\s[:;]\s/)[0], objet.split(/\s[:;,]\s/)[0]];
  for (const o of variantes) for (const ch of [chiffre, ""]) { const s = `Voici ${o} — ${c.libCourt}${ch} ; ${c.quand}, ${c.attente}.`; if (mots(s) <= 28) return s; }
  return `Voici ${variantes[2]} — ${c.libCourt} ; ${c.attente}.`;
}
